import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { ModalConfirm } from '@/components/element/modal/ModalConfirm';
import { MenuButtonGroup } from '@/components/features/SettingMenuButtonGroup';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useEditWorkspaceBasic } from '@/hooks/api/0140_edit_workspace_basic';
import { useWorkspaceCancellation } from '@/hooks/api/0142_workspace_cancellation';
import { useClearCache } from '@/hooks/api/9999_misc';
import { useBase64FromImageFileInput } from '@/hooks/useBase64FromImageFileInput';
import { toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { range } from '@/utils';
import { useEffect, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

type FormInputs = {
  name: string;
  identifier: string;
  to_identifier: string;
  closing_month: number;
  logo_image: string;
};

export const EditWorkspaceBasic = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('スペース基本設定編集'), []);
  useEffect(() => setBreadcrumbs([{ label: `スペース設定`, path: `/w/${_w_!.identifier}/setting` }]), []);

  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, me } = useCurrentWorkspace();

  const { toBase64FromFile, base64Content } = useBase64FromImageFileInput({ aspectRatio: 1 });
  const { mutate, data, isPending, error } = useEditWorkspaceBasic();
  const { clearCache } = useClearCache();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      to_identifier: _w_!.identifier,
      name: _w_!.name,
      closing_month: _w_!.closing_month,
    },
  });

  useEffect(() => {
    if (!me!.admin) {
      navigate('/');
    }
  }, [me]);

  useEffect(() => {
    if (base64Content) setValue('logo_image', base64Content);
  }, [base64Content]);

  useEffect(() => {
    setShowLoading(isPending);
  }, [isPending]);

  useEffect(() => {
    if (data) {
      toastSuccess('変更が完了しました');
      clearCache(); // キャッシュクリア
      navigate(`/w/${getValues('to_identifier')}/setting`);
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <MenuButtonGroup selected={'basic'} />

        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">スペース基本設定編集</h5>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0 row">
                  <ApiErrorMessages error={error} />

                  <div className="col-12 col-sm-4 col-md-3">
                    <div className="avatar avatar-xxl position-relative">
                      <img
                        src={base64Content || _w_!.logo_image_file_url}
                        alt="profile_image"
                        className="w-100 border-radius-md shadow-sm"
                      />
                      <input
                        type="file"
                        id="avater"
                        accept="image/png, image/jpeg"
                        style={{ display: 'none' }}
                        onChange={toBase64FromFile}
                      />
                      <input type="hidden" {...register('logo_image', {})} />
                      <label
                        className="btn btn-sm btn-icon-only bg-gradient-light position-absolute bottom-0 end-0 mb-n2 me-n2"
                        htmlFor="avater"
                      >
                        <i
                          className="fa fa-pen top-0"
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          title=""
                          aria-hidden="true"
                          data-bs-original-title="Edit Image"
                          aria-label="Edit Image"
                        ></i>
                      </label>
                    </div>
                  </div>
                  <div className="col-12 col-sm-8 col-md-9 mt-4 mt-sm-0">
                    <div className="form-group">
                      <label>
                        スペース名<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={30}
                        className={`form-control ${errors.name && 'is-invalid'}`}
                        placeholder={'スペース名'}
                        {...register('name', {
                          required: '入力して下さい。',
                        })}
                      />
                      {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
                    </div>
                    <div className="form-group">
                      <label>
                        スペースID<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={30}
                        className={`form-control ${errors.to_identifier && 'is-invalid'}`}
                        placeholder={'スペースID'}
                        {...register('to_identifier', {
                          required: '入力して下さい。',
                          pattern: {
                            value: /^\w{3,30}$/,
                            message: '半角英数アンダーバー(_)・3〜30文字で入力して下さい。',
                          },
                        })}
                      />
                      <div className="text-xs text-muted mt-1">半角英数アンダーバー(_)・3〜30文字</div>
                      {errors.to_identifier && (
                        <div className="text-xs text-danger mt-1">{errors.to_identifier.message}</div>
                      )}
                    </div>
                    <div className="form-group">
                      <label>
                        決算月<span className="text-danger">*</span>
                      </label>
                      <select className="form-control" {...register('closing_month')}>
                        {range(1, 12).map((m) => (
                          <option key={`month-${m}`} value={m}>
                            {m}月
                          </option>
                        ))}
                      </select>
                      <div className="text-xs text-muted mt-1">決算月を最終月とする一年単位で管理されます</div>
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link to={`/w/${_w_!.identifier}/setting`} className="btn btn-outline-secondary me-2">
                    戻る
                  </Link>
                  <button className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>変更を確定する
                  </button>
                </div>
              </form>
            </div>

            <WorkspaceCancellationElement />
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};

type WorkspaceCancellationFormInputs = {
  name: string;
  identifier: string;
};

const WorkspaceCancellationElement = () => {
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_ } = useCurrentWorkspace();

  const showConfirmButton = useRef<HTMLButtonElement>(null);
  const { data, mutate, isPending, error } = useWorkspaceCancellation();
  const { clearCache } = useClearCache();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<WorkspaceCancellationFormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      name: '',
    },
  });

  useEffect(() => {
    setShowLoading(isPending);
  }, [isPending]);

  useEffect(() => {
    if (data) {
      toastSuccess('解約が完了しました');
      clearCache(); // キャッシュクリア
      navigate(`/`);
    }
  }, [data]);

  const onDeleteClick = () => {
    setShowLoading(true);
    mutate({ identifier: getValues('identifier'), name: getValues('name') });
  };

  const onSubmit: SubmitHandler<WorkspaceCancellationFormInputs> = (data) => {
    if (showConfirmButton.current == null) return;

    showConfirmButton.current.click();
  };

  return (
    <div className="card mb-4" id="workspace-cancellation">
      <div className="card-header d-flex align-items-center">
        <div className="col-md-12">
          <h5 className="mb-1">スペース解約</h5>
          <p className="text-xs mb-0">スペースの解約を行うと、即座にアクセスができなくなり、データの復旧もできません</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="card-body pt-0">
          <ApiErrorMessages error={error} />

          <div className="form-group">
            <label>
              スペース名の確認入力<span className="text-danger">*</span>
            </label>
            <input
              type="text"
              maxLength={30}
              className={`form-control ${errors.name && 'is-invalid'}`}
              placeholder={'スペース名'}
              {...register('name', {
                required: '入力して下さい。',
              })}
            />
            <div className="text-xs text-muted mt-1">確認のため、解約するスペース名を入力してください</div>
            {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
          </div>
        </div>

        <div className="card-footer d-flex align-items-center justify-content-end pt-0">
          <button
            type="button"
            className="btn bg-gradient-dark btn-icon"
            data-bs-toggle="modal"
            data-bs-target={`#delConfirm`}
            style={{ display: 'none' }}
            ref={showConfirmButton}
          >
            解約する
          </button>
          <button type="submit" className="btn bg-gradient-dark btn-icon">
            解約する
          </button>
        </div>
      </form>
      <ModalConfirm
        modalId={'delConfirm'}
        okClicked={onDeleteClick}
        message={
          'スペースの解約を行うと、\n即座にアクセスができなくなり、\nデータの復旧もできません。\n\nまた、年間プランの契約期間が残っている場合でも、\n残りの期間に関わらず契約は終了となります。\nなお、解約月に発生するご請求については、\n解約月の翌月初日に、\n登録されているカードからお引き落としさせていただきます。\n\n解約してよろしいですか？'
        }
      />
    </div>
  );
};
